<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toast-notification";

import { normalizeAmount } from "@/helpers/moneys";
import paymentRequestService from "@/modules/paymentRequest/services/paymentRequestService";
import { PaymentRequestAction, PaymentRequestCreatedOrigin, PaymentRequestStatus } from "@/modules/paymentRequest/types";

import type { PaymentRequestActionSubmit } from "@/modules/paymentRequest/types";

import Alert from "@/dsComponents/Alert/Alert.vue";
import Button from "@/dsComponents/buttons/Button.vue";
import Link from "@/dsComponents/buttons/Link.vue";
import TextareaInput from "@/dsComponents/inputs/TextareaInput.vue";
import TextInputAlt from "@/dsComponents/inputs/TextInputAlt.vue";
import Modal from "@/dsComponents/modals/Modal.vue";

interface PaymentRequestActionModalProps {
    open: boolean;
    paymentRequestId: string;
    requestedAmount: number;
    action: PaymentRequestAction;
    origin?: PaymentRequestCreatedOrigin;
}

const props = defineProps<PaymentRequestActionModalProps>();

const emit = defineEmits<{
    submit: [payload: PaymentRequestActionSubmit];
    close: [];
}>();

const { t } = useI18n();
const toaster = useToast();

const loading = ref(false);
const error = ref("");
const newAmount = ref("");
const comment = ref("");

const actionToStatus: Partial<Record<PaymentRequestAction, PaymentRequestStatus>> = {
    [PaymentRequestAction.ACKNOWLEDGED]: PaymentRequestStatus.ACKNOWLEDGED,
    [PaymentRequestAction.ACCEPTED]: PaymentRequestStatus.ACCEPTED,
    [PaymentRequestAction.PARTIALLY_ACCEPTED]: PaymentRequestStatus.ACCEPTED,
    [PaymentRequestAction.REJECTED]: PaymentRequestStatus.REJECTED,
};

const isRejection = computed(() => props.action === PaymentRequestAction.REJECTED);

const isMaximumRequest = computed(() => props.requestedAmount === 0);

const isAmountMandatory = computed(
    () =>
        props.action === PaymentRequestAction.PARTIALLY_ACCEPTED ||
        (props.action === PaymentRequestAction.ACCEPTED && isMaximumRequest.value)
);

const isAmountEditable = computed(
    () =>
        isAmountMandatory.value ||
        (props.action === PaymentRequestAction.ACCEPTED && props.origin === PaymentRequestCreatedOrigin.AGENCY)
);

const modalTitle = computed(() =>
    isRejection.value ? t("global.actions.refuse") : t(`page.paymentRequest.actionModal.modalTitle.${props.action}`)
);

const modalDescription = computed(() => {
    if (isRejection.value || props.action === PaymentRequestAction.ACKNOWLEDGED) return undefined;

    const displayAmount = props.requestedAmount === 0 ? t("maximum") : `${props.requestedAmount} \u20AC`;

    return props.action === PaymentRequestAction.ACCEPTED && !isMaximumRequest.value
        ? t("page.paymentRequest.actionModal.modalDescription.amountAccepted", { amount: displayAmount })
        : t("page.paymentRequest.actionModal.modalDescription.amountRequested", { amount: displayAmount });
});

const confirmButtonProps = computed(() => ({
    label: isRejection.value ? t("global.actions.refuse") : t("global.actions.confirm"),
    type: isRejection.value ? ("error" as const) : ("primary" as const),
}));

const textAreaProps = computed(() => ({
    label: isRejection.value ? t("page.paymentRequest.actionModal.label.reason") : t("page.paymentRequest.actionModal.label.comment"),
    placeholder: isRejection.value
        ? t("page.paymentRequest.actionModal.placeholder.reason")
        : t("page.paymentRequest.actionModal.placeholder.comment"),
    required: isRejection.value,
}));

const parseAmount = (raw: string): number | undefined => {
    const digits = normalizeAmount(raw).match(/\d+/)?.[0];
    return digits ? Number(digits) : undefined;
};

const resolveAcceptedAmount = (): number | undefined => {
    if (isRejection.value || props.action === PaymentRequestAction.ACKNOWLEDGED) return undefined;
    if (newAmount.value) return parseAmount(newAmount.value);
    return isAmountMandatory.value ? undefined : props.requestedAmount;
};

const handleSubmit = async () => {
    error.value = "";

    if (isRejection.value && !comment.value) {
        error.value = t("page.paymentRequest.actionModal.errors.reason");
        return;
    }

    const status = actionToStatus[props.action];
    if (!status) return;

    const acceptedAmount = resolveAcceptedAmount();
    if (isAmountMandatory.value && acceptedAmount === undefined) {
        error.value = t("page.paymentRequest.actionModal.errors.amountMandatory");
        return;
    }

    loading.value = true;
    try {
        await paymentRequestService.updatePaymentRequest({
            paymentRequestId: props.paymentRequestId,
            body: {
                status,
                comment: comment.value || undefined,
                acceptedAmount,
            },
        });

        emit("submit", {
            status,
            comment: comment.value || undefined,
            acceptedAmount,
        });

        toaster?.open({
            message: t("page.paymentRequest.actionModal.success"),
            type: "success",
            duration: 3000,
        });
    } catch (e) {
        console.error(e);
        toaster?.open({
            message: t("errors.Global.ServerError._default"),
            type: "error",
            duration: 3000,
        });
    } finally {
        loading.value = false;
    }
};
</script>

<template>
    <div class="payment-request-action-modal">
        <Modal :open="open" position="top" width="500px" no-padding :title="modalTitle" :close="!loading" @close="emit('close')">
            <template #default>
                <div class="payment-action-form">
                    <div v-if="modalDescription" class="item-title">{{ modalDescription }}</div>

                    <TextInputAlt
                        v-if="isAmountEditable"
                        v-model="newAmount"
                        variant="grey"
                        size="lg"
                        :placeholder="isMaximumRequest ? t('maximum') : `${requestedAmount}\u20AC`"
                        type="currency"
                    />
                    <TextareaInput v-bind="textAreaProps" v-model="comment" auto-resize no-border />

                    <Alert v-if="error" :message="error" variant="error" />
                </div>
            </template>
            <template #footer>
                <div class="button-container">
                    <Link size="md" :label="t('global.actions.cancel')" type="neutral" :disabled="loading" @click="emit('close')" />
                    <Button v-bind="confirmButtonProps" variant="filled" size="sm" :loading="loading" @click="handleSubmit" />
                </div>
            </template>
        </Modal>
    </div>
</template>

<style scoped lang="scss">
.payment-action-form {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-3xl, 24px);
    padding: var(--spacing-3xl, 24px);
}

.button-container {
    display: flex;
    align-items: center;
    justify-content: end;
    gap: var(--spacing-3xl, 24px);
}
</style>
